import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from '@mui/material';
import { Event } from '../../types/Event';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CancelIcon from '@mui/icons-material/Cancel';

import EventDetails from './EventDetails';
import ParticipantsList from './ParticipantsList';
import WaitingList from './WaitingList';
import RegistrationForm from './RegistrationForm';
import ConfirmationView from './ConfirmationView';
import AnnouncementsList from './AnnouncementsList';

interface JoinModalUIProps {
  event: Event;
  name: string;
  setName: (name: string) => void;
  phone: string;
  spots: number | string;
  error: string | null;
  isLoading: boolean;
  showParticipants: boolean;
  setShowParticipants: (show: boolean) => void;
  showWaitingList: boolean;
  setShowWaitingList: (show: boolean) => void;
  activeStep: number;
  requiresApproval: boolean;
  isWaitlisted: boolean;
  isAuthenticated: boolean;
  currentParticipantCount: number;
  isEventFull: boolean;
  spotsLeft: number;
  isUserParticipant: boolean;
  isCancelling: boolean;
  showCancelConfirm: boolean;
  setShowCancelConfirm: (show: boolean) => void;
  handlePhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSpotsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: React.FormEvent) => void;
  addToCalendar: () => void;
  isFormValid: () => boolean;
  handleCancelEnrollment: () => void;
  onClose: () => void;
  viewOnly?: boolean;
  isPast?: boolean;
  fromUserHome?: boolean;
}

const JoinModalUI: React.FC<JoinModalUIProps> = ({
  event,
  name,
  setName,
  phone,
  spots,
  error,
  isLoading,
  showParticipants,
  setShowParticipants,
  showWaitingList,
  setShowWaitingList,
  activeStep,
  requiresApproval,
  isWaitlisted,
  isAuthenticated,
  currentParticipantCount,
  isEventFull,
  spotsLeft,
  isUserParticipant,
  isCancelling,
  showCancelConfirm,
  setShowCancelConfirm,
  handlePhoneChange,
  handleSpotsChange,
  handleSubmit,
  addToCalendar,
  isFormValid,
  handleCancelEnrollment,
  onClose,
  viewOnly = false,
  isPast = false,
  fromUserHome = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const steps = ['報名資料', '報名完成'];

  const renderRegistrationForm = () => (
    <Box>
      <EventDetails 
        event={event}
        currentParticipantCount={currentParticipantCount}
        spotsLeft={spotsLeft}
        isEventFull={isEventFull}
      />

      <Divider sx={{ my: 2 }} />

      <AnnouncementsList 
        announcements={event.announcements || []} 
        isParticipant={isUserParticipant}
      />

      {event.announcements && event.announcements.length > 0 && (
        <Divider sx={{ my: 2 }} />
      )}

      <ParticipantsList 
        event={event}
        isAuthenticated={isAuthenticated}
        showParticipants={showParticipants}
        setShowParticipants={setShowParticipants}
      />

      {event.waitingList && event.waitingList.length > 0 && (
        <WaitingList 
          event={event}
          isAuthenticated={isAuthenticated}
          showWaitingList={showWaitingList}
          setShowWaitingList={setShowWaitingList}
        />
      )}

      {!viewOnly && (
        <>
          <Divider sx={{ my: 2 }} />

          <RegistrationForm 
            isEventFull={isEventFull}
            error={error}
            name={name}
            setName={setName}
            phone={phone}
            handlePhoneChange={handlePhoneChange}
            spots={spots}
            handleSpotsChange={handleSpotsChange}
            handleSubmit={handleSubmit}
            isPhoneDisabled={isAuthenticated}
          />
        </>
      )}
    </Box>
  );

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderRegistrationForm();
      case 1:
        return (
          <ConfirmationView 
            event={event}
            spots={spots}
            isWaitlisted={isWaitlisted}
            requiresApproval={requiresApproval}
            isAuthenticated={isAuthenticated}
            addToCalendar={addToCalendar}
            onClose={onClose}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
    <Dialog 
      open={true} 
      onClose={activeStep === 1 ? onClose : undefined}
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
      disableScrollLock={false}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
          margin: isMobile ? 0 : undefined,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: 1, 
        borderColor: 'divider',
        py: 2
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          {viewOnly ? '活動詳情' : (activeStep === 0 ? '活動報名' : '報名完成')}
        </Typography>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {!viewOnly && (
          <Stepper activeStep={activeStep} sx={{ mt: 1, mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
        
        {getStepContent(activeStep)}
      </DialogContent>
      
      {viewOnly ? (
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={addToCalendar}
            variant="outlined"
            color="secondary"
            startIcon={<CalendarTodayIcon />}
          >
            加入日曆
          </Button>
          <Box>
            {isUserParticipant && !isPast && (
              <Button
                onClick={() => setShowCancelConfirm(true)}
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                sx={{ mr: 1 }}
              >
                取消報名
              </Button>
            )}
            <Button onClick={onClose} variant="contained">
              關閉
            </Button>
          </Box>
        </DialogActions>
      ) : activeStep === 0 ? (
        <DialogActions sx={{ px: 3, pb: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={addToCalendar}
            variant="outlined"
            color="secondary"
            startIcon={<CalendarTodayIcon />}
          >
            加入日曆
          </Button>
          <Box>
            <Button onClick={onClose} sx={{ mr: 1 }}>
              取消
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={!isFormValid() || isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isLoading ? '處理中...' : isEventFull ? '加入候補名單' : '確認報名'}
            </Button>
          </Box>
        </DialogActions>
      ) : null}
    </Dialog>
    
    {/* Cancel Enrollment Confirmation Dialog */}
    <Dialog
      open={showCancelConfirm}
      onClose={() => setShowCancelConfirm(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>確認取消報名</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          您確定要取消此活動的報名嗎？
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          取消後將釋放您的名額。
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={() => setShowCancelConfirm(false)}
          disabled={isCancelling}
        >
          返回
        </Button>
        <Button
          onClick={handleCancelEnrollment}
          variant="contained"
          color="error"
          disabled={isCancelling}
          startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
        >
          {isCancelling ? '處理中...' : '確認取消'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default JoinModalUI;